import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, Pencil, Trash2, LogOut, Upload, X, Save, 
  Package, Image as ImageIcon, Settings, LayoutDashboard 
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [settings, setSettings] = useState({
    logo_url: '',
    hero_image_url: '',
    hero_title: 'Unbothered Baddie',
    hero_subtitle: 'Luxury Beauty & Accessories'
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'makeup',
    image_url: '',
    stock: 100,
    featured: false
  });

  const categories = ['makeup', 'jewelry', 'body', 'nails', 'hair'];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
      fetchSettings();
    }
  }, [isAuthenticated]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const openNewProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'makeup',
      image_url: '',
      stock: 100,
      featured: false
    });
    setDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url,
      stock: product.stock,
      featured: product.featured
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock)
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const handleSettingsUpdate = async () => {
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSettings(prev => ({ ...prev, logo_url: res.data.image_url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Failed to upload logo');
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSettings(prev => ({ ...prev, hero_image_url: res.data.image_url }));
      toast.success('Hero image uploaded');
    } catch (err) {
      toast.error('Failed to upload image');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-28 pb-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl mb-2" data-testid="admin-dashboard-title">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground text-sm">Manage your store</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-white/20 text-muted-foreground hover:text-gold hover:border-gold"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-8">
          <TabsList className="bg-card border border-white/10">
            <TabsTrigger 
              value="products" 
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Package className="h-4 w-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger 
              value="settings"
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Settings className="h-4 w-4 mr-2" />
              Site Settings
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-heading text-xl">Products ({products.length})</h2>
              <Button
                onClick={openNewProduct}
                className="bg-gold text-black hover:bg-gold-dark text-xs tracking-widest uppercase"
                data-testid="add-product-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-card border border-white/10 p-4">
                    <div className="aspect-video bg-secondary/30 mb-4" />
                    <div className="h-4 bg-secondary/30 w-3/4 mb-2" />
                    <div className="h-4 bg-secondary/30 w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-products-grid">
                {products.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-card border border-white/10 overflow-hidden group"
                  >
                    <div className="aspect-video bg-secondary/30 relative overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      {product.featured && (
                        <span className="absolute top-2 left-2 bg-gold text-black text-[10px] px-2 py-1 uppercase tracking-wider">
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-body text-sm">{product.name}</h3>
                          <p className="text-xs text-muted-foreground capitalize">{product.category}</p>
                        </div>
                        <span className="font-accent text-gold">${product.price.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-4 line-clamp-2">
                        {product.description}
                      </p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Stock: {product.stock}</span>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditProduct(product)}
                            className="h-8 w-8 text-muted-foreground hover:text-gold"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(product.id)}
                            className="h-8 w-8 text-muted-foreground hover:text-red-500"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-2xl space-y-8">
              <div className="bg-card border border-white/10 p-6 space-y-6">
                <h3 className="font-heading text-lg">Site Branding</h3>
                
                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest">Logo</Label>
                  <div className="flex items-center gap-4">
                    {settings.logo_url && (
                      <img
                        src={settings.logo_url}
                        alt="Logo"
                        className="h-12 w-auto object-contain bg-white/5 p-2"
                      />
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <span className="inline-flex items-center px-4 py-2 border border-white/20 text-sm hover:border-gold hover:text-gold transition-colors">
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Logo
                      </span>
                    </label>
                  </div>
                </div>

                {/* Hero Image Upload */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest">Hero Background</Label>
                  <div className="space-y-4">
                    {settings.hero_image_url && (
                      <img
                        src={settings.hero_image_url}
                        alt="Hero"
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleHeroUpload}
                        className="hidden"
                      />
                      <span className="inline-flex items-center px-4 py-2 border border-white/20 text-sm hover:border-gold hover:text-gold transition-colors">
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Upload Hero Image
                      </span>
                    </label>
                  </div>
                </div>

                {/* Hero Title */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest">Hero Title</Label>
                  <Input
                    value={settings.hero_title}
                    onChange={(e) => setSettings(prev => ({ ...prev, hero_title: e.target.value }))}
                    className="bg-transparent border-white/20 focus:border-gold"
                  />
                </div>

                {/* Hero Subtitle */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest">Hero Subtitle</Label>
                  <Input
                    value={settings.hero_subtitle}
                    onChange={(e) => setSettings(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                    className="bg-transparent border-white/20 focus:border-gold"
                  />
                </div>

                <Button
                  onClick={handleSettingsUpdate}
                  className="bg-gold text-black hover:bg-gold-dark text-xs tracking-widest uppercase"
                  data-testid="save-settings-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Product Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Product Image</Label>
                {formData.image_url && (
                  <div className="relative w-full aspect-video bg-secondary/30 mb-2">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                      className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="Image URL"
                    className="bg-transparent border-white/20 focus:border-gold"
                  />
                  <label className="cursor-pointer flex-shrink-0">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      ref={fileInputRef}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/20"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? '...' : <Upload className="h-4 w-4" />}
                    </Button>
                  </label>
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="bg-transparent border-white/20 focus:border-gold"
                  data-testid="product-name-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  required
                  className="bg-transparent border-white/20 focus:border-gold min-h-[100px]"
                  data-testid="product-description-input"
                />
              </div>

              {/* Price & Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest">Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    required
                    className="bg-transparent border-white/20 focus:border-gold"
                    data-testid="product-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest">Stock</Label>
                  <Input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                    required
                    className="bg-transparent border-white/20 focus:border-gold"
                    data-testid="product-stock-input"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger className="bg-transparent border-white/20">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat} className="capitalize">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Featured */}
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest">Featured Product</Label>
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-white/20"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gold text-black hover:bg-gold-dark"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? 'Update' : 'Create'} Product
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
